import { useState, useEffect } from "react";
import { MainLayout } from "../components/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Mail,
  Users,
  UserPlus,
  Trash2,
  RefreshCw,
  Search,
  Download,
  Send,
  CheckCircle,
  XCircle,
  Loader2,
  Calendar,
  Globe,
  Briefcase,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = window.location.origin;

export default function NewsletterManagementPage() {
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, unsubscribed: 0, estetico: 0, altro: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  
  // Send email form
  const [showSendForm, setShowSendForm] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadSubscribers();
  }, []);

  const loadSubscribers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/newsletter/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscribers(data.subscribers || []);
        setStats(data.stats || {});
      } else {
        toast.error("Errore nel caricamento degli iscritti");
      }
    } catch (error) {
      console.error("Errore:", error);
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const deleteSubscriber = async (email) => {
    if (!window.confirm(`Sei sicuro di voler eliminare ${email}?`)) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/newsletter/subscribers/${encodeURIComponent(email)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Iscritto eliminato");
        loadSubscribers();
      } else {
        toast.error("Errore nell'eliminazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const exportSubscribers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/newsletter/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Export completato!");
      }
    } catch (error) {
      toast.error("Errore nell'export");
    }
  };

  const sendCustomNewsletter = async (e) => {
    e.preventDefault();
    if (!emailSubject || !emailContent) {
      toast.error("Compila oggetto e contenuto");
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/newsletter/send-custom`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          subject: emailSubject,
          content: emailContent,
          recipients: selectedSubscribers.length > 0 ? selectedSubscribers : null
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Newsletter inviata!");
        setShowSendForm(false);
        setEmailSubject("");
        setEmailContent("");
        setSelectedSubscribers([]);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'invio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setSending(false);
    }
  };

  const toggleSelectSubscriber = (email) => {
    setSelectedSubscribers(prev => 
      prev.includes(email) 
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const selectAllFiltered = () => {
    const filtered = filteredSubscribers.map(s => s.email);
    setSelectedSubscribers(filtered);
  };

  const filteredSubscribers = subscribers.filter(sub => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      sub.email?.toLowerCase().includes(query) ||
      sub.nome?.toLowerCase().includes(query) ||
      sub.cognome?.toLowerCase().includes(query)
    );
  });

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="newsletter-management-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Mail className="w-7 h-7 text-teal-600" />
              Gestione Newsletter
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestisci gli iscritti e invia comunicazioni
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadSubscribers} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
            <Button variant="outline" onClick={exportSubscribers}>
              <Download className="w-4 h-4 mr-2" />
              Esporta CSV
            </Button>
            <Button onClick={() => setShowSendForm(true)} className="bg-teal-600 hover:bg-teal-700">
              <Send className="w-4 h-4 mr-2" />
              Invia Newsletter
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 rounded-lg">
                  <Users className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Totale Iscritti</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active}</p>
                  <p className="text-xs text-muted-foreground">Attivi</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.unsubscribed}</p>
                  <p className="text-xs text-muted-foreground">Disiscritti</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Briefcase className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.estetico}</p>
                  <p className="text-xs text-muted-foreground">Settore Estetico</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Globe className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.altro}</p>
                  <p className="text-xs text-muted-foreground">Altri Settori</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Send Newsletter Form */}
        {showSendForm && (
          <Card className="border-teal-200 bg-teal-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-teal-600" />
                Invia Newsletter Personalizzata
              </CardTitle>
              <CardDescription>
                {selectedSubscribers.length > 0 
                  ? `Invio a ${selectedSubscribers.length} iscritti selezionati`
                  : `Invio a tutti gli ${stats.active} iscritti attivi`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={sendCustomNewsletter} className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Oggetto Email</label>
                  <Input
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Es: Nuovi contenuti sul benessere..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Contenuto (HTML supportato)</label>
                  <textarea
                    value={emailContent}
                    onChange={(e) => setEmailContent(e.target.value)}
                    placeholder="Scrivi il contenuto della newsletter..."
                    rows={6}
                    className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={sending} className="bg-teal-600 hover:bg-teal-700">
                    {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    {sending ? "Invio in corso..." : "Invia Newsletter"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowSendForm(false)}>
                    Annulla
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Subscribers Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <CardTitle>Lista Iscritti</CardTitle>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Cerca per nome o email..."
                    className="pl-9 w-64"
                  />
                </div>
                {filteredSubscribers.length > 0 && (
                  <Button variant="outline" size="sm" onClick={selectAllFiltered}>
                    Seleziona Tutti ({filteredSubscribers.length})
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
              </div>
            ) : filteredSubscribers.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nessun iscritto trovato</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <input
                          type="checkbox"
                          checked={selectedSubscribers.length === filteredSubscribers.length && filteredSubscribers.length > 0}
                          onChange={() => {
                            if (selectedSubscribers.length === filteredSubscribers.length) {
                              setSelectedSubscribers([]);
                            } else {
                              selectAllFiltered();
                            }
                          }}
                          className="rounded"
                        />
                      </TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Settore</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Data Iscrizione</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSubscribers.map((subscriber) => (
                      <TableRow key={subscriber.email}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedSubscribers.includes(subscriber.email)}
                            onChange={() => toggleSelectSubscriber(subscriber.email)}
                            className="rounded"
                          />
                        </TableCell>
                        <TableCell className="font-medium">{subscriber.email}</TableCell>
                        <TableCell>
                          {subscriber.nome || subscriber.cognome 
                            ? `${subscriber.nome || ''} ${subscriber.cognome || ''}`.trim()
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={subscriber.settore === 'estetico' ? 'default' : 'secondary'}>
                            {subscriber.settore === 'estetico' ? 'Estetico' : 'Altro'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={subscriber.status === 'active' ? 'default' : 'destructive'} 
                                 className={subscriber.status === 'active' ? 'bg-green-100 text-green-700' : ''}>
                            {subscriber.status === 'active' ? 'Attivo' : 'Disiscritto'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {subscriber.subscribed_at 
                            ? new Date(subscriber.subscribed_at).toLocaleDateString('it-IT')
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteSubscriber(subscriber.email)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
